import { t } from 'i18next';
import moment from 'moment';
import { upperFirst } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { FaEdit, FaEnvelope, FaEraser, FaPrint, FaWhatsapp } from 'react-icons/fa';
import { HiMiniDocumentText } from 'react-icons/hi2';
import PDFModal from './components/PDFModals';
import { getPatientInfo } from '../../../redux/hospital';
import { getSharableLink, getVisitPdf, sendEmail } from '../../../redux/visit';


const VisitHeader = ({ isDetails = true, isPastVisit = false, handleLoadVisits, handleClearAll, visitId }) => {
	const { currentPatientDetail } = useSelector(state => state.hospital);
	const { currentHospital, userDetails } = useSelector(state => state.auth);
	const [details, setDetails] = useState(null);
	const [email, setEmail] = useState('');
	const [isMailModalOpen, setIsMailModalOpen] = useState(false);
	const dispatch = useDispatch();
	const { id } = useParams();
	const navigate = useNavigate();

	const handleEditVisit = () => {
		navigate(`/patients/visits/${id}/edit/${visitId}`);
	};

	const handleEmail = () => {
		setIsMailModalOpen(true);
	};

	const handleInputChange = (e) => {
		if (e.target.name === 'email') {
			setEmail(e.target.value);
		}
	};

	const handleSubmitEmail = async() => {
		await dispatch(sendEmail({ email: email, visitId: visitId, pId: id }));
		setIsMailModalOpen(false);
		setEmail('');
	};

	const handleWhatsapp = async() => {
		try {
			const res = await dispatch(getSharableLink({ visitId, pId: id })).unwrap();
			const link = res.response.data.data;
			if (link) {
				window.open(`https://wa.me/?text=${encodeURIComponent(`Check this out: ${link}`)}`, '_blank');
			}
		} catch (error) {
			return error;
		}
	};

	const getDoctorDetails = () => {
		if (details && details.drName) {
			return (
				<p className="text-muted small mb-0">
					By: <strong>Dr. {details ? details.drName : ''}</strong> | {details ? details.doa : ''}
				</p>
			);
		} else {
			return (
				<p className="text-muted small mb-0">
					By: <strong>Dr. {userDetails.first_name} {userDetails.last_name}</strong>
				</p>
			);
		}
	};

	const handlePrint = async() => {
		await dispatch(getVisitPdf({ visitId: visitId, pId: id })).unwrap().then((blob) => {
			const pdfBlobUrl = URL.createObjectURL(blob);

			const iframe = document.createElement('iframe');
			iframe.style.display = 'none';
			iframe.src = pdfBlobUrl;

			document.body.appendChild(iframe);

			iframe.onload = () => {
				iframe.contentWindow?.focus();
				iframe.contentWindow?.print();
			};
		});
	};

	useEffect(() => {
		if (currentHospital.value && id) {
			dispatch(getPatientInfo({ hospital_id: currentHospital.value, p_id: id }));
		}
	}, [id]);

	useEffect(() => {
		if (currentPatientDetail?.patient_info?.panel_details) {
			const { age, gender, number } = currentPatientDetail.patient_info.panel_details.reduce(
				(acc, item) => {
					if (item.title === 'Age') acc.age = item.value ? `${item.value}y` : null;
					if (item.title === 'Sex') acc.gender = item.value;
					if (item.title === 'Mobile Number') acc.number = item.value;
					return acc;
				},
				{ age: null, gender: null, number: null }
			);
			const patientName = upperFirst(currentPatientDetail?.patient_info?.panel_title?.name ?? '');
			const drName = upperFirst(currentPatientDetail?.patient_info?.panel_title?.att_dr);
			const doa = moment(currentPatientDetail?.patient_info?.panel_title?.doa).format('DD-MM-YYYY');
			setDetails({ age, gender, number, patientName, drName, doa });
		}
	}
	, [currentPatientDetail]);

	return (
		<Card.Header className="border-0 py-3" style={{ backgroundColor: '#EDEDF3' }}>
			<Row className="justify-content-between align-items-center">
				<Col xs={12} sm={8} className="mb-2 mb-sm-0">
					<Row className="align-items-center">
						{!isPastVisit && (
							<Col xs="auto" className="me-4">
								<h2 className="h5 mb-1 text-primary">{details ? details.patientName : ''}</h2>
								<p className="text-muted mb-0">
									{details && (() => {
										const values = [details.age, details.gender, details.number].filter(Boolean);
										return values.map((item, index) => (
											<span key={index} className="me-2 small">
												{item}
												{index !== values.length - 1 && ' |'}
											</span>
										));
									})()}
								</p>
							</Col>
						)}
						<Col xs="auto">
							{getDoctorDetails()}
						</Col>
					</Row>
				</Col>

				{!isDetails ?
					(
						<Col xs={12} sm="auto" className="text-end">
							<Button variant="success" size="sm" className="me-2" onClick={handleLoadVisits}>
								<HiMiniDocumentText className="me-1" />
								{t('VISIT.LOAD_VISITS')}
							</Button>
							<Button variant="primary" size="sm" className="me-2" onClick={handleClearAll}>
								<FaEraser className="me-1" />
								{t('VISIT.CLEAR_ALL')}
							</Button>
						</Col>
					) : (
						<Col xs={12} sm="auto" className="text-end">
							{!isPastVisit && (
								<Button className='btn btn-success btn-sm me-2' onClick={handleEditVisit}>
									<FaEdit className="me-1" />
									{t('GENERAL.EDIT')}
								</Button>
							)}
							<Button variant="primary" size="sm" className="me-2" onClick={handleEmail}>
								<FaEnvelope className="me-1" />
								{t('GENERAL.EMAIL')}
							</Button>
							<Button variant="primary" size="sm" className="me-2" onClick={handleWhatsapp}>
								<FaWhatsapp className="me-1" />
								{t('GENERAL.WHATSAPP')}
							</Button>
							<Button variant="primary" size="sm" onClick={handlePrint}>
								<FaPrint className="me-1" />
								{t('GENERAL.PRINT')}
							</Button>
						</Col>
					)}
			</Row>
			<PDFModal show={isMailModalOpen} close={() => { setIsMailModalOpen(false); setEmail(''); }} title="Email" handleInputChange={handleInputChange} inputValue={email} inputType="email" handleSubmit={handleSubmitEmail	} />
		</Card.Header>
	);
};

export default VisitHeader;